import { DEFAULT_MAP_ZOOM } from "@/config/map";
import { useTranslations } from "next-intl";
import { googleMapsLoader } from "@/lib/googleMapsLoader";
import type { Coords } from "@/types/places";
import { coordsToLatLng, DEFAULT_COORDS } from "@/utils/location";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseInitializeGoogleMapOptions {
  apiKey?: string;
  mapId?: string;
  initialCenter?: Coords | null;
  initialZoom?: number;
  mapOptions?: Partial<google.maps.MapOptions>;
  onMapClick?: () => void;
}

interface UseInitializeGoogleMapResult {
  map: google.maps.Map | null;
  mapRef: React.RefObject<HTMLDivElement | null>;
  mapsInitialized: boolean;
  error: string | null;
  isLoading: boolean;
}

/**
 * Custom hook to load Google Maps script and initialize a map instance
 */
export function useInitializeGoogleMap({
  apiKey,
  mapId = "e4fe7fffb4d188dacd605e1e",
  initialCenter = null,
  initialZoom = DEFAULT_MAP_ZOOM,
  mapOptions = {},
  onMapClick,
}: UseInitializeGoogleMapOptions = {}): UseInitializeGoogleMapResult {
  const t = useTranslations();
  const key = apiKey || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Callback to handle when Google Maps is loaded
  const handleMapsLoaded = useCallback(() => {
    setMapsInitialized(true);
    setIsLoading(false);
    setError(null);
  }, []);

  // Load Google Maps script
  useEffect(() => {
    if (!key?.trim()) {
      const isDevelopment = process.env.NODE_ENV === "development";
      setError(
        isDevelopment
          ? t("googleMap.errorApiKeyMissing")
          : t("googleMap.errorTemporarilyUnavailable"),
      );
      setIsLoading(false);
      return;
    }

    // If already loaded, call callback immediately
    if (googleMapsLoader.isLoaded()) {
      handleMapsLoaded();
      return;
    }

    // Load Google Maps
    googleMapsLoader.load(key, handleMapsLoaded).catch((err) => {
      setError(t("googleMap.errorLoadFailed", { error: err.message }));
      setIsLoading(false);
    });

    // Cleanup callback on unmount
    return () => {
      googleMapsLoader.removeCallback(handleMapsLoaded);
    };
  }, [key, handleMapsLoaded, t]);

  // Initialize map instance when Google Maps is ready
  useEffect(() => {
    if (!mapRef.current || map || !mapsInitialized) return;

    try {
      const center = initialCenter || DEFAULT_COORDS;
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        mapId,
        center: coordsToLatLng(center),
        zoom: initialZoom,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: "greedy",
        ...mapOptions,
      });

      setMap(mapInstance);
    } catch (err) {
      setError(t("googleMap.errorInitializationFailed"));
      console.error("Failed to initialize Google Maps:", err);
    }
  }, [mapsInitialized, map, mapId, initialCenter, initialZoom, mapOptions, t]);

  // Add click listener to map
  useEffect(() => {
    if (!map || !onMapClick) return;

    const listener = map.addListener("click", () => {
      onMapClick();
    });

    return () => {
      listener.remove();
    };
  }, [map, onMapClick]);

  return { map, mapRef, mapsInitialized, error, isLoading };
}
